import React from "react";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { Row, Col } from "reactstrap";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import EditorContainer from "../../components/EditorContainer";
import { createUser, updateUser, getUser } from "../../api/users";
import { getRolesDomain } from "../../auth/roles";
import Address from "../../components/Address";
import { addressSchema } from "../../utils/customValidators";

const schema = () =>
  Yup.object({
    name: Yup.string()
      .required("Nome è un campo obbligatorio")
      .nullable(),
    surname: Yup.string()
      .required("Cognome è un campo obbligatorio")
      .nullable(),
    taxCode: Yup.string().required("Il codice fiscale deve essere di 16 caratteri").max(16, "Il codice fiscale deve essere di 16 caratteri").min(16, "Il codice fiscale deve essere di 16 caratteri"),
    vatCode: Yup.string()
      .vatCode()
      .when("taxCode", (taxCode, schema) => {
        return taxCode
          ? schema
          : schema.required(
            "Inserire il Codice Fiscale o la Partita Iva"
          );
      }),
    email: Yup.string()
      .required("Email è un campo obbligatorio")
      .email("Inserire una email valida"),
    phone: Yup.string()
      .required("Telefono è un campo obbligatorio")
      .nullable(),
    role: Yup.string()
      .required("Ruolo è un campo obbligatorio")
      .nullable(),
    invCode: Yup.string().nullable()
  });

const UserEditor = ({ readOnly, edit }) => {
  const { id } = useParams();

  const onCreate = async user => {
    const result = await createUser(user);
    return result;
  };

  const onLoad = async id => {
    const user = await getUser(id);
    return user;
  };

  const onUpdate = async user => {
    const result = await updateUser(user);
    return result;
  };

  return (
    <EditorContainer
      key={id ? `${id}-user` : "new-user"}
      canSave={true}
      schema={schema}
      readOnly={readOnly}
      onCreate={onCreate}
      onUpdate={onUpdate}
      onLoad={onLoad}
      optionalNav={{ link: "/admin/users", text: "Utenti" }}
      redirRoot="/admin/users"
      title={el => id ? `${el.name + " " + el.surname || ""} - Ruolo ${(el.role.charAt(0).toUpperCase() + el.role.slice(1)).toUpperCase() || ""}` : "NUOVO UTENTE"}
      children={(frmProps, containerHandleChange) => (
        <Row>
          <Col sm="6" xs="12">
            <strong className="mt-2">Cognome</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.surname ? "p-error" : ""}
              value={frmProps.values["surname"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("surname", e)}
            />
            {frmProps.errors.surname ? (<div className="invalid-feedback">{frmProps.errors.surname}</div>) : null}
          </Col>
          <Col sm="6" xs="12">
            <strong className="mt-2">Nome</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.name ? "p-error" : ""}
              value={frmProps.values["name"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("name", e)}
            />
            {frmProps.errors.name ? (<div className="invalid-feedback">{frmProps.errors.name}</div>) : null}
          </Col>
          <Col sm="6" xs="12">
            <strong className="mt-2">Email</strong>
            <InputText
              keyfilter="email"
              disabled={readOnly || edit ? true : false}
              className={frmProps.errors.email ? "p-error" : ""}
              value={frmProps.values["email"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("email", e)}
            />
            {frmProps.errors.email ? (<div className="invalid-feedback">{frmProps.errors.email}</div>) : null}
          </Col>
          <Col sm="6" xs="12">
            <strong className="mt-2">Ruolo</strong>
            <Dropdown
              filter
              filterBy="label"
              id="role"
              disabled={readOnly || edit ? true : false}
              className={frmProps.errors.role ? "p-error" : ""}
              value={frmProps.values["role"]}
              showClear={true}
              style={{ width: "100%" }}
              options={getRolesDomain()}
              onChange={e => containerHandleChange("role", e)}
            />
            {frmProps.errors.role ? (<div className="invalid-feedback">{frmProps.errors.role}</div>) : null}
          </Col>
          <Col sm="6" xs="12">
            <strong className="mt-2">Codice Fiscale</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.taxCode ? "p-error" : ""}
              value={frmProps.values["taxCode"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("taxCode", e)}
            />
            {frmProps.errors.taxCode ? (<div className="invalid-feedback">{frmProps.errors.taxCode}</div>) : null}
          </Col>
          <Col sm="6" xs="12">
            <strong className="mt-2">Partita Iva</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.vatCode ? "p-error" : ""}
              value={frmProps.values["vatCode"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("vatCode", e)}
            />
            {frmProps.errors.vatCode ? (<div className="invalid-feedback">{frmProps.errors.vatCode}</div>) : null}
          </Col>

          <Address
            errors={frmProps.errors.address || {}}
            address={frmProps.values["address"] || {}}
            onChange={e => containerHandleChange("address", e)}
          />
          <Col sm="6" xs="12">
            <strong className="mt-2">Telefono/Cellulare</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.phone ? "p-error" : ""}
              value={frmProps.values["phone"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("phone", e)}
            />
            {frmProps.errors.phone ? (<div className="invalid-feedback">{frmProps.errors.phone}</div>) : null}
          </Col>
          <Col sm="6" xs="12">
            <strong className="mt-2">Cod. Fatturazione Elettronica</strong>
            <InputText
              disabled={readOnly ? true : false}
              className={frmProps.errors.invCode ? "p-error" : ""}
              value={frmProps.values["invCode"] || ""}
              style={{ width: "100%" }}
              onChange={e => containerHandleChange("invCode", e)}
            />
          </Col>
          <Col sm="6" xs="12">
            <strong className="mt-2">Note</strong>
            <InputTextarea
              className={frmProps.errors.note ? "p-error" : ""}
              value={frmProps.values["note"] || ""}
              style={{ width: "100%" }}
              rows={2}
              onChange={e => containerHandleChange("note", e)}
            />
          </Col>
        </Row>
      )}
    />
  );
};
export default UserEditor;
